
import { useEffect, useRef, useState } from 'react';

export default function TeamSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const card = cardsRef.current[index];
    if (!card) return;
    
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const team = [
    {
      name: 'Chukwuemeka Okonkwo',
      role: 'Founder & CEO',
      bio: 'Trained in structural engineering at TU Munich, Emeka brings 20+ years of experience in luxury construction across Europe and Africa.',
      image: 'https://readdy.ai/api/search-image?query=distinguished%20Black%20African%20male%20CEO%20in%20elegant%20charcoal%20business%20suit%2C%20Nigerian%20construction%20company%20founder%2C%20confident%20professional%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20executive%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team101&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'emeka@buildfast.ng',
      expertise: 'Structural Engineering',
      projects: '150+'
    },
    {
      name: 'Adebayo Adeleke',
      role: 'Chief Operations Officer',
      bio: 'With a background in project management from ETH Zurich, Adebayo ensures every BuildFast project is delivered on time and to perfection.',
      image: 'https://readdy.ai/api/search-image?query=professional%20Black%20African%20male%20operations%20director%20in%20modern%20navy%20business%20attire%2C%20Nigerian%20construction%20executive%2C%20confident%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20leadership%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team102&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'adebayo@buildfast.ng',
      expertise: 'Project Management',
      projects: '200+'
    },
    {
      name: 'Ngozi Eze',
      role: 'Head of Interior Design',
      bio: 'A graduate of the Architectural Association in London, Ngozi leads our design team with an eye for timeless elegance and functional beauty.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20Black%20African%20female%20interior%20designer%20in%20sophisticated%20professional%20attire%2C%20Nigerian%20design%20director%2C%20confident%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20creative%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team103&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'ngozi@buildfast.ng',
      expertise: 'Interior Architecture',
      projects: '120+'
    },
    {
      name: 'Ibrahim Yusuf',
      role: 'Master Craftsman Lead',
      bio: 'Trained in traditional carpentry in Germany, Ibrahim oversees our team of skilled artisans, ensuring every detail meets our exacting standards.',
      image: 'https://readdy.ai/api/search-image?query=skilled%20Black%20African%20male%20master%20craftsman%20in%20professional%20work%20attire%2C%20Nigerian%20construction%20expert%2C%20confident%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20expert%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team104&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'ibrahim@buildfast.ng',
      expertise: 'Fine Carpentry',
      projects: '300+'
    },
    {
      name: 'Amaka Nwosu',
      role: 'Client Relations Director',
      bio: 'With a decade of experience in luxury hospitality, Amaka ensures every client interaction reflects the BuildFast commitment to excellence.',
      image: 'https://readdy.ai/api/search-image?query=professional%20Black%20African%20female%20client%20relations%20director%20in%20elegant%20business%20attire%2C%20Nigerian%20hospitality%20executive%2C%20warm%20confident%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20approachable%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team105&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'amaka@buildfast.ng',
      expertise: 'Client Experience',
      projects: '180+'
    },
    {
      name: 'Oluwaseun Balogun',
      role: 'Quality Assurance Manager',
      bio: 'A certified quality control specialist from Stuttgart, Seun implements rigorous inspection protocols that guarantee flawless execution.',
      image: 'https://readdy.ai/api/search-image?query=professional%20Black%20African%20male%20quality%20manager%20in%20modern%20business%20attire%2C%20Nigerian%20construction%20specialist%2C%20confident%20portrait%2C%20dramatic%20studio%20lighting%2C%20dark%20moody%20background%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20precision%20presence%2C%20cinematic%20quality&width=400&height=500&seq=team106&orientation=portrait',
      linkedin: 'https://linkedin.com',
      email: 'seun@buildfast.ng',
      expertise: 'Quality Control',
      projects: '250+'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 lg:py-36 bg-[#0A0A0A] overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #C9A86C 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Floating Geometric Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 border border-[#C9A86C]/20 rotate-45 animate-pulse" />
      <div className="absolute bottom-40 right-20 w-24 h-24 border border-[#C9A86C]/10 rotate-12" />
      <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-[#C9A86C]/5 rotate-45" />

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#C9A86C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#C9A86C]/3 rounded-full blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-16 md:mb-20 lg:mb-24 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="flex items-center justify-center gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="w-12 md:w-20 h-px bg-gradient-to-r from-transparent to-[#C9A86C]" />
            <div className="relative">
              <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.3em] md:tracking-[0.4em] uppercase">Meet Our Team</span>
              <div className="absolute -bottom-2 left-0 right-0 h-px bg-[#C9A86C]/30" />
            </div>
            <div className="w-12 md:w-20 h-px bg-gradient-to-l from-transparent to-[#C9A86C]" />
          </div>
          
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight">
            The People Behind
            <span className="block mt-2 bg-gradient-to-r from-[#C9A86C] via-[#E8D5B0] to-[#C9A86C] bg-clip-text text-transparent">
              The Precision
            </span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[#8A8A8A] max-w-3xl mx-auto leading-relaxed">
            Our leadership team combines international training with deep local expertise to deliver world-class results
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {team.map((member, index) => (
            <div
              key={index}
              ref={el => cardsRef.current[index] = el}
              className={`group relative cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
              } ${activeCard === index ? 'z-20 scale-[1.02]' : 'z-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onClick={() => setActiveCard(activeCard === index ? null : index)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              onMouseMove={(e) => handleMouseMove(e, index)}
            >
              {/* Card Glow Effect */}
              <div 
                className={`absolute -inset-1 bg-gradient-to-r from-[#C9A86C] via-[#E8D5B0] to-[#C9A86C] rounded-lg opacity-0 blur-sm transition-opacity duration-500 ${
                  hoveredCard === index || activeCard === index ? 'opacity-60' : ''
                }`}
              />

              {/* Spotlight Effect */}
              {hoveredCard === index && (
                <div 
                  className="absolute inset-0 rounded-lg opacity-30 pointer-events-none z-10"
                  style={{
                    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(201, 168, 108, 0.4) 0%, transparent 50%)`
                  }}
                />
              )}

              {/* Main Card */}
              <div className={`relative bg-[#141414] rounded-lg overflow-hidden border transition-all duration-500 ${
                activeCard === index ? 'border-[#C9A86C]' : 'border-[#2A2A2A] hover:border-[#C9A86C]/50'
              }`}>
                {/* Image Container */}
                <div className="relative overflow-hidden">
                  <div className="w-full h-[320px] md:h-[380px]">
                    <img
                      src={member.image}
                      alt={member.name}
                      className={`w-full h-full object-cover object-top transition-all duration-700 ${
                        hoveredCard === index ? 'scale-110 brightness-110' : 'scale-100'
                      }`}
                    />
                  </div>

                  {/* Gradient Overlays */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/20 to-transparent" />
                  <div className={`absolute inset-0 bg-[#C9A86C]/10 transition-opacity duration-500 ${
                    hoveredCard === index ? 'opacity-100' : 'opacity-0'
                  }`} />

                  {/* Expertise Badge */}
                  <div className={`absolute top-4 left-4 px-3 py-1.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A86C]/30 rounded-full transition-all duration-500 ${
                    hoveredCard === index || activeCard === index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}>
                    <span className="text-[#C9A86C] text-xs tracking-wider">{member.expertise}</span>
                  </div>

                  {/* Projects Count */}
                  <div className={`absolute top-4 right-4 transition-all duration-500 ${
                    hoveredCard === index || activeCard === index ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                  }`}>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#C9A86C]">{member.projects}</div>
                      <div className="text-[10px] text-white/60 uppercase tracking-wider">Projects</div>
                    </div>
                  </div>

                  {/* Social Links - Floating */}
                  <div className={`absolute bottom-4 left-4 flex gap-2 transition-all duration-500 ${
                    hoveredCard === index || activeCard === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}>
                    <a 
                      href={member.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A86C]/30 rounded-full flex items-center justify-center hover:bg-[#C9A86C] hover:border-[#C9A86C] transition-all duration-300 cursor-pointer group/icon"
                    >
                      <i className="ri-linkedin-line text-[#C9A86C] group-hover/icon:text-[#0A0A0A] text-lg transition-colors" />
                    </a>
                    <a 
                      href={`mailto:${member.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A86C]/30 rounded-full flex items-center justify-center hover:bg-[#C9A86C] hover:border-[#C9A86C] transition-all duration-300 cursor-pointer group/icon"
                    >
                      <i className="ri-mail-line text-[#C9A86C] group-hover/icon:text-[#0A0A0A] text-lg transition-colors" />
                    </a>
                  </div>

                  {/* Expand Indicator */}
                  <div className={`absolute bottom-4 right-4 w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-500 ${
                    activeCard === index 
                      ? 'bg-[#C9A86C] border-[#C9A86C] rotate-45' 
                      : 'bg-transparent border-[#C9A86C]/50 rotate-0'
                  }`}>
                    <i className={`ri-add-line text-lg transition-colors ${
                      activeCard === index ? 'text-[#0A0A0A]' : 'text-[#C9A86C]'
                    }`} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 md:p-8">
                  {/* Name with animated underline */}
                  <div className="relative inline-block mb-2">
                    <h3 className="text-xl md:text-2xl font-serif text-white">{member.name}</h3>
                    <div className={`absolute -bottom-1 left-0 h-0.5 bg-[#C9A86C] transition-all duration-500 ${
                      hoveredCard === index || activeCard === index ? 'w-full' : 'w-0'
                    }`} />
                  </div>
                  
                  <div className="text-sm text-[#C9A86C] mb-4 uppercase tracking-[0.15em]">{member.role}</div>
                  
                  {/* Bio with expand animation */}
                  <div className={`overflow-hidden transition-all duration-500 ${
                    activeCard === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <p className="text-sm md:text-base text-[#8A8A8A] leading-relaxed pb-4">
                      {member.bio}
                    </p>
                  </div>

                  {/* Click hint */}
                  <div className={`flex items-center gap-2 text-xs text-[#6A6A6A] transition-opacity duration-300 ${
                    activeCard === index ? 'opacity-0' : 'opacity-100'
                  }`}>
                    <i className="ri-cursor-line" />
                    <span>Click to learn more</span>
                  </div>
                </div>

                {/* Corner Accents */}
                <div className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#C9A86C] transition-all duration-500 ${
                  hoveredCard === index || activeCard === index ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`} />
                <div className={`absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#C9A86C] transition-all duration-500 ${
                  hoveredCard === index || activeCard === index ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`} />
                <div className={`absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#C9A86C] transition-all duration-500 ${
                  hoveredCard === index || activeCard === index ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`} />
                <div className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#C9A86C] transition-all duration-500 ${
                  hoveredCard === index || activeCard === index ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`} />
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className={`mt-20 md:mt-24 lg:mt-32 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="relative group">
            {/* CTA Glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#C9A86C]/20 via-[#C9A86C]/40 to-[#C9A86C]/20 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative bg-[#141414] border border-[#2A2A2A] group-hover:border-[#C9A86C]/30 rounded-lg p-10 md:p-14 text-center transition-all duration-500">
              {/* Decorative Elements */}
              <div className="absolute top-6 left-6 w-12 h-12 border border-[#C9A86C]/20 rotate-45 group-hover:rotate-90 transition-transform duration-700" />
              <div className="absolute bottom-6 right-6 w-12 h-12 border border-[#C9A86C]/20 rotate-45 group-hover:rotate-90 transition-transform duration-700" />

              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-8 h-px bg-[#C9A86C]/50" />
                <i className="ri-team-line text-[#C9A86C] text-xl" />
                <div className="w-8 h-px bg-[#C9A86C]/50" />
              </div>

              <h3 className="text-3xl md:text-4xl font-serif text-white mb-4">Join Our Team</h3>
              <p className="text-base md:text-lg text-[#8A8A8A] mb-8 max-w-2xl mx-auto leading-relaxed">
                We&apos;re always looking for talented craftsmen, designers, and project managers who share our commitment to excellence.
              </p>
              
              <a 
                href="mailto:careers@buildfast.ng"
                className="inline-flex items-center gap-3 px-10 py-4 bg-transparent border-2 border-[#C9A86C] text-[#C9A86C] hover:bg-[#C9A86C] hover:text-[#0A0A0A] transition-all duration-500 cursor-pointer whitespace-nowrap group/btn rounded-sm"
              >
                <span className="text-sm md:text-base tracking-[0.15em] uppercase font-medium">View Open Positions</span>
                <i className="ri-arrow-right-line text-lg group-hover/btn:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
